/**
 * Sourcing behaviour per purchaser (Akhil, 2026-09-19: "how many supplier comparisons has he
 * used, and which supplier does he choose most — so we can check the purchaser is not being
 * lazy"). Pure: the page hands in the enquiries in range and four flat lists read from the
 * ERP (`RFQ`, `Supplier Quotation`, `Supplier Quote Comparison`, `Comparison Allocation`), and
 * gets one row per purchaser.
 *
 * ---- What "lazy" looks like in the data ----
 *
 *   - asks ONE supplier per enquiry (`single_supplier_enquiries` against `enquiries`) —
 *     nothing to compare, so the price is whatever that supplier said;
 *   - has quotes from several suppliers and never opens the workbench (`comparisons_used`
 *     against `comparable_enquiries`, the enquiries with two or more quotes);
 *   - allocates to the same supplier whatever the offers (`top_suppliers[0].share`).
 *
 * None of these is a verdict on its own — a category with one real supplier looks the same —
 * which is why the page shows the numbers beside each other and names the supplier rather than
 * printing a score.
 */

import type { EnquiryFact } from './types'

export interface SourcingSources {
  rfqs: readonly { enquiry: string; supplier: string }[]
  quotations: readonly { enquiry: string; supplier: string }[]
  comparisons: readonly { name: string; enquiry: string }[]
  allocations: readonly { parent: string; supplier: string; enquiry_row_no: string }[]
}

export interface SupplierShare {
  supplier: string
  lines: number
  /** Of the purchaser's allocated lines, 0–1. */
  share: number
}

export interface PurchaserSourcing {
  purchaser: string
  purchaser_name: string
  enquiries: number
  rfqs: number
  suppliers_asked: string[]
  /** RFQs per enquiry that had at least one — how widely they ask. */
  avg_suppliers_asked: number
  /** Enquiries where exactly one supplier was asked. */
  single_supplier_enquiries: number
  quotes_received: number
  /** Enquiries with two or more supplier quotations — where a comparison meant something. */
  comparable_enquiries: number
  /** Comparable enquiries with a saved comparison — never more than `comparable_enquiries`. */
  comparisons_used: number
  /** Every enquiry with a saved comparison, comparable or not (one-quote and stock lines too). */
  comparisons_total: number
  allocated_lines: number
  /** Most-chosen first. */
  top_suppliers: SupplierShare[]
}

export function buildSourcing(facts: readonly EnquiryFact[], sources: SourcingSources): PurchaserSourcing[] {
  const byPurchaser = new Map<string, EnquiryFact[]>()
  for (const f of facts) {
    const list = byPurchaser.get(f.purchaser)
    if (list) list.push(f)
    else byPurchaser.set(f.purchaser, [f])
  }

  const rfqsByEnquiry = groupBy(sources.rfqs, (r) => r.enquiry)
  const quotesByEnquiry = groupBy(sources.quotations, (q) => q.enquiry)
  const comparisonsByEnquiry = groupBy(sources.comparisons, (c) => c.enquiry)
  const allocationsByComparison = groupBy(sources.allocations, (a) => a.parent)

  const rows: PurchaserSourcing[] = []
  for (const [purchaser, list] of byPurchaser) {
    let rfqs = 0
    let withRfq = 0
    let single = 0
    let quotes = 0
    let comparable = 0
    let compared = 0
    let comparedAll = 0
    const asked = new Set<string>()
    const chosen = new Map<string, number>()
    for (const f of list) {
      const r = rfqsByEnquiry.get(f.id) ?? []
      rfqs += r.length
      if (r.length > 0) withRfq += 1
      const distinctAsked = new Set(r.map((x) => x.supplier))
      if (distinctAsked.size === 1) single += 1
      distinctAsked.forEach((s) => asked.add(s))
      const q = quotesByEnquiry.get(f.id) ?? []
      quotes += q.length
      const isComparable = new Set(q.map((x) => x.supplier)).size >= 2
      if (isComparable) comparable += 1
      const cmps = comparisonsByEnquiry.get(f.id) ?? []
      if (cmps.length > 0) {
        comparedAll += 1
        if (isComparable) compared += 1
      }
      for (const c of cmps) {
        for (const a of allocationsByComparison.get(c.name) ?? []) chosen.set(a.supplier, (chosen.get(a.supplier) ?? 0) + 1)
      }
    }
    const allocated = [...chosen.values()].reduce((s, n) => s + n, 0)
    rows.push({
      purchaser,
      purchaser_name: list[0].purchaser_name,
      enquiries: list.length,
      rfqs,
      suppliers_asked: [...asked].sort(),
      avg_suppliers_asked: withRfq > 0 ? rfqs / withRfq : 0,
      single_supplier_enquiries: single,
      quotes_received: quotes,
      comparable_enquiries: comparable,
      comparisons_used: compared,
      comparisons_total: comparedAll,
      allocated_lines: allocated,
      top_suppliers: [...chosen]
        .map(([supplier, lines]) => ({ supplier, lines, share: allocated > 0 ? lines / allocated : 0 }))
        .sort((a, b) => b.lines - a.lines || a.supplier.localeCompare(b.supplier)),
    })
  }
  return rows.sort((a, b) => b.enquiries - a.enquiries || a.purchaser.localeCompare(b.purchaser))
}

/** The manager's filters (2026-09-19). An unset or empty key means "any". */
export interface FactFilters {
  purchaser?: string
  customer?: string
  enquiry_type?: string
  status?: string
}

export function filterFacts(facts: readonly EnquiryFact[], filters: FactFilters): EnquiryFact[] {
  return facts.filter(
    (f) =>
      (!filters.purchaser || f.purchaser === filters.purchaser) &&
      (!filters.customer || f.customer === filters.customer) &&
      (!filters.enquiry_type || f.enquiry_type === filters.enquiry_type) &&
      (!filters.status || f.status === filters.status),
  )
}

function groupBy<T>(rows: readonly T[], key: (row: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>()
  for (const row of rows) {
    const k = key(row)
    const list = out.get(k)
    if (list) list.push(row)
    else out.set(k, [row])
  }
  return out
}
